import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { BadgeDollarSign, CalendarCheck, CalendarClock, Inbox, Landmark, LogOut, MessageSquare, Palette, Settings, Store, UserRound, Users } from "lucide-react";
import { queueCountQuery } from "@web/lib/queries-automation";
import { unreadCountQuery } from "@web/lib/queries-leads";
import { signOut, useMe } from "@web/lib/session";
import { Button, Page, Row, RowGroup, Stack, StatusChip } from "@web/ui";

export function More() {
  const me = useMe();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const unread = useQuery(unreadCountQuery).data ?? 0;
  const queued = useQuery(queueCountQuery).data ?? 0;

  async function onSignOut() {
    setSigningOut(true);
    await signOut();
    navigate({ to: "/signin", replace: true });
  }

  return (
    <Page>
      <Stack gap={4}>
        <RowGroup>
          <Row icon={<UserRound aria-hidden size={22} />} title={me.user.name ?? me.user.email} subtitle={`${me.user.email} · ${me.organization.name} · ${me.member.role}`} wrap />
        </RowGroup>
        <RowGroup>
          <Row icon={<CalendarClock aria-hidden size={22} />} title="Tours" subtitle="Upcoming visits, follow-ups and tour history" onClick={() => navigate({ to: "/tours" })} />
          <Row icon={<CalendarCheck aria-hidden size={22} />} title="Events" subtitle="Every event, organized by day" onClick={() => navigate({ to: "/events" })} />
          <Row icon={<Inbox aria-hidden size={22} />} title="Review queue" subtitle="Automated messages waiting for a person to send them" trailing={queued > 0 ? <StatusChip tone="accent">{queued}</StatusChip> : undefined} onClick={() => navigate({ to: "/more/queue" })} />
          <Row icon={<Store aria-hidden size={22} />} title="Vendors" subtitle="Caterers, florists, DJs — who you work with and how to reach them" onClick={() => navigate({ to: "/more/vendors" })} />
          <Row icon={<MessageSquare aria-hidden size={22} />} title="Messages" subtitle="Texts, emails and calls with clients" trailing={unread > 0 ? <StatusChip tone="accent">{unread}</StatusChip> : undefined} onClick={() => navigate({ to: "/more/messages" })} />
          <Row icon={<Landmark aria-hidden size={22} />} title="Accounting" subtitle="Collected, outstanding, due lists" onClick={() => navigate({ to: "/more/accounting" })} />
          <Row icon={<BadgeDollarSign aria-hidden size={22} />} title={me.companyAdmin ? "Commissions" : "Your commissions"} subtitle="What is payable, and what has been paid" onClick={() => navigate({ to: "/more/commissions" })} />
          {me.companyAdmin && <Row icon={<Settings aria-hidden size={22} />} title="Settings" subtitle="Company, venues, catalog, event types" onClick={() => navigate({ to: "/settings" })} />}
          {me.companyAdmin && <Row icon={<Users aria-hidden size={22} />} title="Users" subtitle="Invite, roles, venues" onClick={() => navigate({ to: "/settings/users" })} />}
          {me.env === "staging" && <Row icon={<Palette aria-hidden size={22} />} title="Component gallery" subtitle="Staging only · /dev/ui" onClick={() => navigate({ to: "/dev/ui" })} />}
        </RowGroup>
        <Button variant="secondary" icon={<LogOut aria-hidden size={20} />} loading={signingOut} onClick={onSignOut}>
          Sign out
        </Button>
      </Stack>
    </Page>
  );
}
